import React, { useState, useEffect } from "react";
import {
  Car,
  Calendar,
  MapPin,
  User,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  X,
  FileText,
  Sparkles,
  LogOut,
  ChevronDown,
  ChevronRight,
  Building2,
  ClipboardList,
} from "lucide-react";
import { ref, push, set, onValue } from "firebase/database";
import { auth, database } from "./firebase";

const PRESTATIONS = [
  "Nettoyage intérieur",
  "Nettoyage extérieur",
  "Nettoyage complet (intérieur + extérieur)",
  "Lustrage / Polish",
  "Pressing des sièges",
  "Désinfection véhicule",
  "Nettoyage moteur",
  "Rénovation phares",
];

const STATUS_CONFIG = {
  "En attente": {
    bg: "bg-yellow-50",
    text: "text-yellow-700",
    badge: "bg-yellow-100",
    border: "border-yellow-400",
    icon: Clock,
  },
  "En cours": {
    bg: "bg-blue-50",
    text: "text-blue-700",
    badge: "bg-blue-100",
    border: "border-blue-400",
    icon: AlertCircle,
  },
  Terminé: {
    bg: "bg-green-50",
    text: "text-green-700",
    badge: "bg-green-100",
    border: "border-green-400",
    icon: CheckCircle,
  },
  Annulé: {
    bg: "bg-red-50",
    text: "text-red-700",
    badge: "bg-red-100",
    border: "border-red-400",
    icon: X,
  },
};

function BookingCard({ booking }) {
  const config = STATUS_CONFIG[booking.state] || STATUS_CONFIG["En attente"];
  const StatusIcon = config.icon;
  return (
    <div
      className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border-l-4 ${config.border}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-indigo-500" />
            <span className="text-sm font-semibold text-indigo-600">
              {booking.prestationType || "Prestation"}
            </span>
          </div>
          <span
            className={`${config.badge} ${config.text} px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1`}
          >
            <StatusIcon size={12} />
            {booking.state}
          </span>
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">
          {booking.carName || "—"}
        </h3>
        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Car size={14} className="text-gray-400" />
            {booking.carImmatriculation || "—"}
          </div>
          <div className="flex items-center gap-2">
            <User size={14} className="text-gray-400" />
            {booking.nameClient || "—"}
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-gray-400" />
            {booking.site || "—"}
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-gray-400" />
            {booking.date || "—"}
            {booking.heure && ` à ${booking.heure}`}
          </div>
        </div>
        {booking.note && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-start gap-2 text-sm text-gray-500">
            <FileText size={14} className="mt-0.5 text-gray-400 shrink-0" />
            {booking.note}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ConciergeBooking({ user, conciergeName }) {
  const [bookings, setBookings] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [viewTab, setViewTab] = useState("sites");
  const [expandedSites, setExpandedSites] = useState({});
  const [formData, setFormData] = useState({
    prestationType: "",
    carName: "",
    carImmatriculation: "",
    nameClient: "",
    site: "",
    date: new Date().toISOString().split("T")[0],
    heure: "09:00",
    note: "",
  });

  useEffect(() => {
    const sitesUnsub = onValue(ref(database, "sites"), (snap) => {
      const d = snap.val();
      if (d) {
        setAllSites(
          Object.entries(d).map(([id, s]) => ({ id, name: s.name, conciergerie: s.conciergerie || "" }))
        );
      } else {
        setAllSites([]);
      }
    });
    return () => sitesUnsub();
  }, []);

  const mySites = allSites.filter(
    (s) => s.conciergerie && s.conciergerie.toLowerCase() === (conciergeName || "").toLowerCase()
  );
  const mySiteNames = mySites.map((s) => s.name);

  useEffect(() => {
    const pendingRef = ref(database, "tasksPending");
    const tasksRef = ref(database, "tasks");
    let pendingData = [];
    let validatedData = [];

    const belongs = (t) =>
      t.conciergeId === user.uid ||
      (t.site && mySiteNames.includes(t.site));

    const merge = () => {
      setBookings(
        [...pendingData, ...validatedData].sort(
          (a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
        )
      );
    };

    const unsubPending = onValue(pendingRef, (snapshot) => {
      const data = snapshot.val();
      pendingData = data
        ? Object.keys(data)
            .map((id) => ({ id, source: "tasksPending", ...data[id] }))
            .filter(belongs)
        : [];
      merge();
    });

    const unsubTasks = onValue(tasksRef, (snapshot) => {
      const data = snapshot.val();
      validatedData = data
        ? Object.keys(data)
            .map((id) => ({ id, source: "tasks", ...data[id] }))
            .filter(belongs)
        : [];
      merge();
    });

    return () => {
      unsubPending();
      unsubTasks();
    };
  }, [user.uid, mySiteNames.join(",")]);

  const filterByDate = (list) =>
    list.filter((b) => {
      const d = b.date || "";
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });

  const dateFilteredBookings = filterByDate(bookings);

  const bookingsBySite = {};
  mySites.forEach((s) => {
    bookingsBySite[s.name] = [];
  });
  dateFilteredBookings.forEach((b) => {
    if (b.site && bookingsBySite[b.site] !== undefined) {
      bookingsBySite[b.site].push(b);
    }
  });

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (!formData.prestationType || !formData.carName || !formData.carImmatriculation || !formData.site || !formData.date) {
      alert("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    setSubmitting(true);
    try {
      const bookingData = {
        ...formData,
        state: "En attente",
        conciergeId: user.uid,
        conciergeName: conciergeName || user.email,
        conciergeEmail: user.email,
        createdAt: new Date().toISOString(),
      };

      const newRef = push(ref(database, "tasksPending"));
      await set(newRef, bookingData);

      setFormData({
        prestationType: "",
        carName: "",
        carImmatriculation: "",
        nameClient: "",
        site: "",
        date: new Date().toISOString().split("T")[0],
        heure: "09:00",
        note: "",
      });
      setShowBookingForm(false);
      setSuccessMessage("Votre réservation a été envoyée avec succès !");
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (error) {
      alert("Erreur lors de la réservation : " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredBookings = dateFilteredBookings.filter(
    (b) => !filterStatus || b.state === filterStatus
  );

  const countByStatus = (status) => dateFilteredBookings.filter((b) => b.state === status).length;

  const clearDateFilters = () => {
    setDateFrom("");
    setDateTo("");
  };
  const hasDateFilter = dateFrom || dateTo;

  return (
    <div className="min-h-screen bg-gray-50">
      <div
        className="text-white shadow-lg"
        style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
      >
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold mb-1">MIVIA Services</h1>
              <p className="text-blue-200 text-sm">
                Espace Conciergerie — {conciergeName || user.email}
              </p>
            </div>
            <button
              onClick={() => auth.signOut()}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition flex items-center gap-2 text-sm"
            >
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl mb-6 flex items-center gap-3 animate-fade-in">
            <CheckCircle size={20} />
            <span className="font-medium">{successMessage}</span>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-yellow-400">
            <p className="text-xs text-gray-500 font-semibold mb-1">EN ATTENTE</p>
            <p className="text-3xl font-bold text-gray-800">{countByStatus("En attente")}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-blue-400">
            <p className="text-xs text-gray-500 font-semibold mb-1">EN COURS</p>
            <p className="text-3xl font-bold text-gray-800">{countByStatus("En cours")}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-green-400">
            <p className="text-xs text-gray-500 font-semibold mb-1">TERMINÉES</p>
            <p className="text-3xl font-bold text-gray-800">{countByStatus("Terminé")}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-indigo-400">
            <p className="text-xs text-gray-500 font-semibold mb-1">MES SITES</p>
            <p className="text-3xl font-bold text-gray-800">{mySites.length}</p>
          </div>
        </div>

        {/* Onglets */}
        <div className="flex items-center gap-1 bg-white rounded-xl shadow-sm p-1.5 mb-6">
          <button
            onClick={() => setViewTab("sites")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${
              viewTab === "sites"
                ? "bg-indigo-600 text-white shadow-md"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <MapPin size={16} /> Mes Sites
          </button>
          <button
            onClick={() => setViewTab("bookings")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${
              viewTab === "bookings"
                ? "bg-indigo-600 text-white shadow-md"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <ClipboardList size={16} /> Toutes les Réservations
          </button>
        </div>

        {/* Filtres dates + bouton nouvelle réservation */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <Calendar size={14} className="text-gray-400" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-sm border-none outline-none bg-transparent text-gray-700 w-[130px]"
                placeholder="Du"
              />
            </div>
            <span className="text-gray-400 text-sm">→</span>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <Calendar size={14} className="text-gray-400" />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-sm border-none outline-none bg-transparent text-gray-700 w-[130px]"
              />
            </div>
            {hasDateFilter && (
              <button
                onClick={clearDateFilters}
                className="text-xs text-gray-500 hover:text-red-500 flex items-center gap-1 transition"
              >
                <X size={14} /> Effacer
              </button>
            )}
          </div>
          <button
            onClick={() => setShowBookingForm(true)}
            className="text-white px-5 py-2.5 rounded-lg font-semibold transition shadow-lg flex items-center gap-2 text-sm shrink-0"
            style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
          >
            <Plus size={18} /> Nouvelle Réservation
          </button>
        </div>

        {/* Vue par site */}
        {viewTab === "sites" && (
          <>
            {mySites.length > 0 ? (
              <div className="space-y-3">
                {mySites.map((site) => {
                  const siteBookings = (bookingsBySite[site.name] || [])
                    .slice()
                    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
                  const pending = siteBookings.filter((b) => b.state === "En attente").length;
                  const inProgress = siteBookings.filter((b) => b.state === "En cours").length;
                  const done = siteBookings.filter((b) => b.state === "Terminé").length;
                  const isOpen = !!expandedSites[site.id];
                  const toggle = () =>
                    setExpandedSites((prev) => ({ ...prev, [site.id]: !prev[site.id] }));
                  return (
                    <div key={site.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                      <button
                        onClick={toggle}
                        className="w-full p-5 flex items-center justify-between hover:bg-gray-50 transition cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                            <Building2 size={20} className="text-indigo-600" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-lg font-bold text-gray-800">{site.name}</h3>
                            <p className="text-xs text-gray-400">{siteBookings.length} prestation{siteBookings.length !== 1 ? "s" : ""}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 text-xs font-semibold">
                            {pending > 0 && (
                              <span className="bg-yellow-100 text-yellow-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                                <Clock size={12} /> {pending}
                              </span>
                            )}
                            {inProgress > 0 && (
                              <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                                <AlertCircle size={12} /> {inProgress}
                              </span>
                            )}
                            {done > 0 && (
                              <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                                <CheckCircle size={12} /> {done}
                              </span>
                            )}
                          </div>
                          {isOpen ? (
                            <ChevronDown size={20} className="text-gray-400" />
                          ) : (
                            <ChevronRight size={20} className="text-gray-400" />
                          )}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="border-t border-gray-100">
                          {siteBookings.length > 0 ? (
                            <div className="divide-y divide-gray-50">
                              {siteBookings.map((booking) => {
                                const config = STATUS_CONFIG[booking.state] || STATUS_CONFIG["En attente"];
                                const StatusIcon = config.icon;
                                return (
                                  <div key={booking.id} className="px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition">
                                    <div className={`w-2 h-2 rounded-full shrink-0 ${config.text.replace("text-", "bg-")}`} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-semibold text-gray-800 truncate">
                                          {booking.prestationType || "Prestation"}
                                        </span>
                                        <span className={`${config.badge} ${config.text} px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1 shrink-0`}>
                                          <StatusIcon size={10} />
                                          {booking.state}
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                                        <span className="flex items-center gap-1">
                                          <Car size={12} className="text-gray-400" />
                                          {booking.carName || "—"} · {booking.carImmatriculation || "—"}
                                        </span>
                                        {booking.nameClient && (
                                          <span className="flex items-center gap-1">
                                            <User size={12} className="text-gray-400" />
                                            {booking.nameClient}
                                          </span>
                                        )}
                                        {booking.note && (
                                          <span className="flex items-center gap-1 text-gray-400">
                                            <FileText size={12} />
                                            {booking.note.length > 40 ? booking.note.slice(0, 40) + "…" : booking.note}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                      <p className="text-sm font-medium text-gray-700">{booking.date || "—"}</p>
                                      {booking.heure && <p className="text-xs text-gray-400">{booking.heure}</p>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="p-6 text-center">
                              <Car size={28} className="mx-auto text-gray-200 mb-2" />
                              <p className="text-gray-400 text-sm">Aucune prestation sur ce site</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm">
                <MapPin size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-400 text-lg mb-2">Aucun site associé</p>
                <p className="text-gray-400 text-sm">
                  Aucun site n'est encore rattaché à votre conciergerie.
                </p>
              </div>
            )}
          </>
        )}

        {/* Vue toutes les réservations */}
        {viewTab === "bookings" && (
          <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                Mes Réservations
                <span className="text-sm font-normal text-gray-400 ml-2">({filteredBookings.length})</span>
              </h2>
              <div className="relative">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="appearance-none bg-white border border-gray-200 rounded-lg pl-4 pr-10 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                >
                  <option value="">Tous les statuts</option>
                  {Object.keys(STATUS_CONFIG).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            {filteredBookings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredBookings.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm">
                <Car size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-400 text-lg mb-2">Aucune réservation</p>
                <p className="text-gray-400 text-sm">
                  Cliquez sur "Nouvelle Réservation" pour réserver une prestation.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {showBookingForm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowBookingForm(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="text-white p-6 rounded-t-2xl"
              style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">Nouvelle Réservation</h2>
                  <p className="text-blue-200 text-sm mt-1">Réservez votre prestation de nettoyage</p>
                </div>
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmitBooking} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Type de prestation *
                </label>
                <select
                  value={formData.prestationType}
                  onChange={(e) => setFormData({ ...formData, prestationType: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
                >
                  <option value="">Sélectionnez une prestation</option>
                  {PRESTATIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Véhicule *
                  </label>
                  <input
                    type="text"
                    value={formData.carName}
                    onChange={(e) => setFormData({ ...formData, carName: e.target.value })}
                    placeholder="Ex : Mercedes Classe E"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Immatriculation *
                  </label>
                  <input
                    type="text"
                    value={formData.carImmatriculation}
                    onChange={(e) =>
                      setFormData({ ...formData, carImmatriculation: e.target.value.toUpperCase() })
                    }
                    placeholder="Ex : AB-123-CD"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nom du client
                </label>
                <input
                  type="text"
                  value={formData.nameClient}
                  onChange={(e) => setFormData({ ...formData, nameClient: e.target.value })}
                  placeholder="Nom du client"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Site *</label>
                <select
                  value={formData.site}
                  onChange={(e) => setFormData({ ...formData, site: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
                >
                  <option value="">Sélectionnez un site</option>
                  {mySiteNames.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Heure souhaitée
                  </label>
                  <input
                    type="time"
                    value={formData.heure}
                    onChange={(e) => setFormData({ ...formData, heure: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes complémentaires
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Précisions, demandes particulières..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowBookingForm(false)}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 text-white px-6 py-3 rounded-lg font-semibold transition shadow-lg disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
                >
                  {submitting ? "Envoi..." : "Réserver"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
